import { Phone, ArrowRight, Home as HomeIcon, ChevronRight, Check, ShieldCheck, Bug, Rat, Worm, Clock, MapPin, Award, HelpCircle, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsAppButton";
import BackToTop from "../components/BackToTop";
import { Button } from "../components/ui/button";
import { useSEO } from "../lib/useSEO";
import { SITE } from "../lib/site";

const SLUG = "entreprise-nuisibles-creuse";
const CANONICAL = `https://kreuse-exterieur.fr/${SLUG}`;

const NUISIBLES = [
  {
    icon: Bug,
    title: "Frelons asiatiques et frelons européens",
    desc: "Destruction de nids en hauteur avec combinaison intégrale et perche télescopique. Intervention rapide, y compris en urgence.",
    keywords: "frelons Creuse, nid de frelons asiatiques Guéret, Vespa velutina 23",
  },
  {
    icon: Bug,
    title: "Guêpes et abeilles",
    desc: "Neutralisation de nids de guêpes autour des habitations, sur les toitures et dans les cheminées. Traitement sécurisé.",
    keywords: "guêpes Creuse, nid guêpes toiture",
  },
  {
    icon: Worm,
    title: "Chenilles processionnaires (pin & chêne)",
    desc: "Échenillage manuel, pose d'éco-pièges à collerette. Danger pour les enfants, les animaux domestiques et les allergiques.",
    keywords: "chenilles processionnaires Creuse, échenillage 23",
  },
  {
    icon: Rat,
    title: "Rats, souris et mulots",
    desc: "Diagnostic complet, piégeage professionnel et traitement raticide raisonné. Solution durable pour habitations et dépendances.",
    keywords: "dératisation Creuse, rats souris 23, mulots Guéret",
  },
  {
    icon: Bug,
    title: "Cafards et blattes",
    desc: "Désinsectisation ciblée des logements et locaux professionnels infestés. Méthodes homologuées, gel appâts et pulvérisation.",
    keywords: "désinsectisation Creuse, cafards blattes 23",
  },
  {
    icon: Bug,
    title: "Punaises de lit",
    desc: "Traitement à la vapeur sèche 180 °C ou biocide homologué, protocole en 3 passages, garantie de résultat. Idéal pour hôtels, gîtes et particuliers.",
    keywords: "punaises de lit Creuse, traitement punaises Guéret, vapeur sèche 23",
  },
  {
    icon: Bug,
    title: "Vrillettes du bois et insectes xylophages",
    desc: "Traitement curatif de charpentes, poutres, meubles anciens et parquets contre vrillettes, capricornes et autres insectes xylophages. Injection et pulvérisation homologuées.",
    keywords: "vrillettes du bois Creuse, traitement charpente 23, insectes xylophages Guéret, capricorne bois",
  },
  {
    icon: Rat,
    title: "Fouines, blaireaux et autres nuisibles",
    desc: "Solutions adaptées contre les fouines dans les combles, blaireaux au jardin et autres animaux nuisibles.",
    keywords: "fouines Creuse, blaireau jardin 23",
  },
];

const STEPS = [
  {
    num: "01",
    title: "Diagnostic gratuit",
    desc: "Vous nous contactez par téléphone, WhatsApp ou formulaire. On vous répond sous 24h en moyenne — dans la journée pour les urgences.",
  },
  {
    num: "02",
    title: "Devis clair",
    desc: "Nous vous transmettons un devis gratuit et détaillé, sans surprise. Notre certification Certibiocide garantit un traitement conforme.",
  },
  {
    num: "03",
    title: "Intervention en Creuse",
    desc: "Nous nous déplaçons sur place avec le matériel adapté (combinaison, perche télescopique, produits biocides homologués).",
  },
  {
    num: "04",
    title: "Suivi 24h",
    desc: "Après traitement, nous vérifions le résultat 24h plus tard et vous garantissons une solution durable.",
  },
];

const CITIES = ["Guéret", "La Souterraine", "Aubusson", "Bourganeuf", "Boussac", "La Celle-Dunoise", "Felletin", "Bénévent-l'Abbaye", "Dun-le-Palestel", "Sainte-Feyre"];

const FAQ = [
  {
    q: "Combien coûte la destruction d'un nid de frelons asiatiques en Creuse ?",
    a: "Le tarif dépend de la hauteur et de l'accessibilité du nid. En moyenne, comptez entre 90 € et 180 € pour un nid situé jusqu'à 12 m de hauteur. Nous fournissons un devis gratuit et détaillé après photo ou visite. Aucun coût caché — le prix annoncé est le prix payé.",
  },
  {
    q: "Intervenez-vous en urgence pour les frelons ou les guêpes ?",
    a: "Oui. Pour les urgences (nid actif dans une habitation, présence d'enfants ou d'allergiques, activité intense), nous intervenons dans la journée ou sous 24 h en pleine saison (juin à octobre). Contactez-nous par téléphone au 07 80 54 11 15 ou par WhatsApp.",
  },
  {
    q: "Êtes-vous certifiés pour appliquer des traitements biocides ?",
    a: "Oui. K'REUSE EXTÉRIEUR est titulaire du certificat Certibiocide, agrément d'État obligatoire depuis 2015 pour appliquer des produits biocides à titre professionnel. Nous utilisons exclusivement des produits homologués et respectons les dosages réglementaires.",
  },
  {
    q: "Dans quelles communes intervenez-vous pour la lutte anti-nuisibles ?",
    a: "Nous intervenons dans toute la Creuse (23) : Guéret, La Souterraine, Aubusson, Bourganeuf, Boussac, Felletin, Bénévent-l'Abbaye, Dun-le-Palestel, Sainte-Feyre et toutes les communes du département. Nous couvrons également les départements limitrophes (Haute-Vienne 87, Indre 36, Allier 03).",
  },
  {
    q: "Comment reconnaître un nid de frelons asiatiques ?",
    a: "Le nid de frelons asiatiques a une forme sphérique ou en poire, de couleur beige/marron clair, avec une ouverture latérale (jamais en bas). Il peut atteindre 80 cm de diamètre et se trouve souvent en hauteur (arbres, cheminées). Consultez notre guide détaillé pour identifier un nid avec certitude.",
  },
  {
    q: "Que faire en cas de piqûre de chenilles processionnaires ?",
    a: "Ne touchez pas la chenille et ne frottez pas la zone touchée. Rincez abondamment à l'eau claire, retirez les poils avec du ruban adhésif, et consultez un médecin en cas de réaction cutanée sévère, respiratoire ou oculaire. Pour un chien, urgence vétérinaire immédiate (risque de nécrose de la langue).",
  },
  {
    q: "Proposez-vous un contrat annuel pour la dératisation ?",
    a: "Oui, nous proposons des contrats de dératisation raisonnée pour les particuliers (habitations rurales, dépendances) et professionnels (gîtes, exploitations agricoles, commerces). 2 à 4 visites par an selon l'infestation, avec suivi documenté. Devis personnalisé sur demande.",
  },
  {
    q: "Quels moyens de paiement acceptez-vous ?",
    a: "Nous acceptons les espèces, chèques et virements bancaires. Facture émise systématiquement, avec mention du taux de TVA applicable. La lutte anti-nuisibles n'ouvre pas droit au crédit d'impôt 50 % (seul l'entretien de jardin y ouvre droit).",
  },
];

export default function EntrepriseNuisiblesCreuse() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": ["LocalBusiness", "PestControl"],
        "@id": "https://kreuse-exterieur.fr/#pest-control",
        name: "K'REUSE EXTÉRIEUR — Anti-nuisibles Creuse",
        description: "Entreprise anti-nuisibles agréée Certibiocide en Creuse (23). Destruction de nids de frelons, guêpes, chenilles processionnaires, dératisation, désinsectisation. Devis gratuit.",
        telephone: "+33780541115",
        url: CANONICAL,
        image: SITE.logo,
        address: {
          "@type": "PostalAddress",
          streetAddress: "27 rue des Pradelles",
          addressLocality: "La Celle-Dunoise",
          postalCode: "23800",
          addressRegion: "Creuse",
          addressCountry: "FR",
        },
        areaServed: [
          { "@type": "AdministrativeArea", name: "Creuse (23)" },
          { "@type": "AdministrativeArea", name: "Haute-Vienne (87)" },
          { "@type": "AdministrativeArea", name: "Indre (36)" },
          { "@type": "AdministrativeArea", name: "Allier (03)" },
        ],
        priceRange: "€€",
        openingHoursSpecification: [
          {
            "@type": "OpeningHoursSpecification",
            name: "Paysagisme",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
            opens: "09:00",
            closes: "18:00",
          },
          {
            "@type": "OpeningHoursSpecification",
            name: "Anti-nuisibles (urgences)",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            opens: "00:00",
            closes: "23:59",
          },
        ],
        paymentAccepted: "Espèces, Chèque, Virement bancaire",
      },
      {
        "@type": "Service",
        "@id": `${CANONICAL}#service`,
        name: "Lutte anti-nuisibles en Creuse",
        serviceType: "Lutte anti-nuisibles",
        description: "Destruction de nids de frelons et guêpes, dératisation, désinsectisation, punaises de lit et chenilles processionnaires en Creuse (23).",
        url: CANONICAL,
        provider: { "@id": "https://kreuse-exterieur.fr/#pest-control" },
        areaServed: { "@type": "AdministrativeArea", name: "Creuse (23)" },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://kreuse-exterieur.fr/" },
          { "@type": "ListItem", position: 2, name: "Entreprise anti-nuisibles Creuse", item: CANONICAL },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  useSEO({
    title: "Anti-nuisibles Creuse (23) — Entreprise agréée | K'REUSE",
    description: "Entreprise anti-nuisibles Creuse Certibiocide. Frelons, guêpes, chenilles, dératisation, désinsectisation. Guéret, Aubusson. Devis 24h.",
    canonical: CANONICAL,
    jsonLd,
  });

  return (
    <div className="min-h-screen overflow-x-hidden">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-14 md:pt-40 md:pb-20 overflow-hidden">
          <div className="absolute inset-0 -z-10 pointer-events-none">
            <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-[#C8E6C9] opacity-40 blur-3xl" />
            <div className="absolute top-40 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#FFE0E0] opacity-50 blur-3xl" />
          </div>
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <nav aria-label="Breadcrumb" className="flex items-center text-sm text-[#5C705E] mb-6 flex-wrap">
              <Link to="/" className="flex items-center gap-1 hover:text-[#1B5E20]">
                <HomeIcon className="w-3.5 h-3.5" />
                Accueil
              </Link>
              <ChevronRight className="w-3.5 h-3.5 mx-2" />
              <span className="text-[#1B5E20] font-medium">Anti-nuisibles Creuse</span>
            </nav>

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E5EBE5] shadow-sm mb-6">
              <ShieldCheck className="w-4 h-4 text-[#1B5E20]" />
              <span className="text-sm font-medium text-[#1A2E1C]">
                Entreprise certifiée Certibiocide — Agrément d'État
              </span>
            </div>

            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#1A2E1C] leading-[1.05]">
              Entreprise <span className="font-medium text-[#1B5E20]">anti-nuisibles</span> en Creuse (23)
            </h1>

            <p className="mt-6 text-lg text-[#5C705E] leading-relaxed max-w-3xl">
              Frelons asiatiques, guêpes, chenilles processionnaires, rats, cafards, fouines&nbsp;: <strong>K'REUSE EXTÉRIEUR</strong> traite tous les nuisibles en Creuse et intervient rapidement à <strong>Guéret</strong>, <strong>La Souterraine</strong>, <strong>Aubusson</strong>, <strong>Bourganeuf</strong> et dans tout le département 23. Devis gratuit, intervention rapide, traitements biocides homologués.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild data-testid="pest-cta-call" className="bg-[#1B5E20] hover:bg-[#164d1a] text-white rounded-full px-7 py-6 text-base font-medium">
                <a href={`tel:${SITE.phoneRaw}`}>
                  <Phone className="mr-2 w-4 h-4" />
                  {SITE.phone}
                </a>
              </Button>
              <Button asChild variant="outline" data-testid="pest-cta-quote" className="bg-white border-[#1B5E20] text-[#1B5E20] hover:bg-[#E8F5E9] rounded-full px-7 py-6 text-base font-medium">
                <a href="/#contact">
                  Demander un devis gratuit
                  <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </Button>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-2xl">
              <div className="flex items-start gap-2">
                <Clock className="w-5 h-5 text-[#1B5E20] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-['Outfit'] font-semibold text-sm text-[#1A2E1C]">Sous 24h</div>
                  <div className="text-xs text-[#5C705E]">réponse à votre demande</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Award className="w-5 h-5 text-[#1B5E20] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-['Outfit'] font-semibold text-sm text-[#1A2E1C]">Certibiocide</div>
                  <div className="text-xs text-[#5C705E]">agréé d'État</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="w-5 h-5 text-[#1B5E20] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-['Outfit'] font-semibold text-sm text-[#1A2E1C]">Toute la Creuse</div>
                  <div className="text-xs text-[#5C705E]">+ départements limitrophes</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Nuisibles list */}
        <section className="py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <div className="text-xs tracking-[0.25em] uppercase text-[#1B5E20] font-medium mb-3">
                Traitement des nuisibles
              </div>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-light text-[#1A2E1C] tracking-tight">
                Tous les <span className="font-medium text-[#1B5E20]">nuisibles</span> que nous traitons en Creuse
              </h2>
              <p className="text-[#5C705E] mt-5 max-w-2xl mx-auto">
                Une seule entreprise pour l'ensemble de vos besoins de <strong>désinsectisation</strong>, <strong>dératisation</strong> et destruction de nids en Creuse (23).
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {NUISIBLES.map((n) => {
                const Icon = n.icon;
                return (
                  <div key={n.title} className="bg-white rounded-3xl p-6 border border-[#E5EBE5] hover:shadow-md hover:-translate-y-0.5 transition-all">
                    <div className="w-12 h-12 rounded-2xl bg-[#E8F5E9] flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-[#1B5E20]" />
                    </div>
                    <h3 className="font-['Outfit'] text-lg font-semibold text-[#1A2E1C] mb-2">{n.title}</h3>
                    <p className="text-sm text-[#5C705E] leading-relaxed">{n.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Why us */}
        <section className="py-16 md:py-20 bg-gradient-to-b from-[#E8F5E9]/40 to-transparent">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="grid lg:grid-cols-2 gap-10 items-start">
              <div>
                <div className="text-xs tracking-[0.25em] uppercase text-[#1B5E20] font-medium mb-3">
                  Pourquoi nous choisir
                </div>
                <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-light text-[#1A2E1C] tracking-tight">
                  Une entreprise <span className="font-medium text-[#1B5E20]">locale et certifiée</span> en Creuse
                </h2>
                <p className="text-[#5C705E] mt-5 leading-relaxed">
                  Basés à La Celle-Dunoise, nous connaissons parfaitement le territoire creusois — ses spécificités climatiques, ses espèces invasives (frelons asiatiques en particulier) et les problématiques rencontrées par ses habitants.
                </p>
                <p className="text-[#5C705E] mt-4 leading-relaxed">
                  Notre agrément <strong>Certibiocide</strong> (agrément d'État obligatoire pour l'application de traitements biocides) garantit un travail conforme à la réglementation, avec des produits homologués et un dosage raisonné.
                </p>
              </div>

              <ul className="space-y-3">
                {[
                  "Entreprise creusoise implantée à La Celle-Dunoise (23800)",
                  "Certification Certibiocide (agrément d'État)",
                  "Devis gratuit et sans engagement sous 24h",
                  "Intervention en urgence en journée pour frelons/guêpes",
                  "Traitement biocide raisonné et respectueux",
                  "Suivi 24h après intervention pour garantir le résultat",
                  "Tarifs clairs — pas de mauvaises surprises",
                  "Zone d'intervention élargie (23 + 87 + 36 + 03)",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 p-4 bg-white border border-[#E5EBE5] rounded-2xl">
                    <div className="w-6 h-6 rounded-full bg-[#E8F5E9] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-[#1B5E20]" />
                    </div>
                    <span className="text-sm text-[#1A2E1C] flex-1">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-16 md:py-20">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <div className="text-xs tracking-[0.25em] uppercase text-[#1B5E20] font-medium mb-3">
                Notre méthode
              </div>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-light text-[#1A2E1C] tracking-tight">
                Comment se déroule une <span className="font-medium text-[#1B5E20]">intervention</span>
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {STEPS.map((s) => (
                <div key={s.num} className="bg-white rounded-3xl p-6 border border-[#E5EBE5]">
                  <div className="font-['Outfit'] text-3xl font-light text-[#1B5E20]/60 mb-2">{s.num}</div>
                  <h3 className="font-['Outfit'] font-semibold text-[#1A2E1C] mb-2">{s.title}</h3>
                  <p className="text-sm text-[#5C705E] leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Cities */}
        <section className="py-14">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-light text-[#1A2E1C] tracking-tight mb-6">
              Anti-nuisibles à <span className="font-medium text-[#1B5E20]">Guéret, La Souterraine, Aubusson</span> et partout en Creuse
            </h2>
            <p className="text-[#5C705E] mb-6 max-w-3xl">
              Nous nous déplaçons dans tout le département 23 pour vos problèmes de nuisibles. Communes principales desservies :
            </p>
            <div className="flex flex-wrap gap-2">
              {CITIES.map((c) => (
                <span key={c} className="px-3 py-1.5 rounded-full bg-white border border-[#E5EBE5] text-[#1A2E1C] text-sm">
                  {c}
                </span>
              ))}
            </div>
            <p className="text-sm text-[#5C705E] mt-6">
              Voir aussi&nbsp;: <Link to="/destruction-frelons-creuse" className="text-[#1B5E20] underline">destruction de nids de frelons en Creuse</Link> · <Link to="/zone-intervention-creuse-et-limitrophes" className="text-[#1B5E20] underline">zone d'intervention élargie (87, 36, 03)</Link>
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20 bg-gradient-to-b from-transparent to-[#E8F5E9]/40" data-testid="pest-faq-section">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#1B5E20] font-medium mb-3">
                <HelpCircle className="w-4 h-4" />
                Questions fréquentes
              </div>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-light text-[#1A2E1C] tracking-tight">
                Vos questions sur les <span className="font-medium text-[#1B5E20]">nuisibles en Creuse</span>
              </h2>
            </div>
            <div className="space-y-3">
              {FAQ.map((item, i) => (
                <details
                  key={item.q}
                  data-testid={`pest-faq-${i}`}
                  className="group bg-white rounded-2xl border border-[#E5EBE5] hover:shadow-md transition-shadow overflow-hidden"
                >
                  <summary className="flex items-center justify-between gap-4 p-5 cursor-pointer list-none">
                    <h3 className="font-['Outfit'] font-semibold text-[#1A2E1C] text-base sm:text-lg">
                      {item.q}
                    </h3>
                    <ChevronDown className="w-5 h-5 text-[#1B5E20] flex-shrink-0 transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="px-5 pb-5 -mt-1 text-sm text-[#5C705E] leading-relaxed">
                    {item.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-br from-[#D32F2F] to-[#B71C1C] text-white">
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-light tracking-tight">
              Un nuisible chez vous&nbsp;? On intervient vite.
            </h2>
            <p className="mt-4 text-white/85">
              Réponse sous 24h en moyenne, et même dans la journée pour les urgences (frelons, guêpes en saison).
            </p>
            <div className="mt-7 flex flex-wrap gap-3 justify-center">
              <Button asChild className="bg-white text-[#D32F2F] hover:bg-white/95 rounded-full px-6 py-6 font-medium">
                <a href={`tel:${SITE.phoneRaw}`}>
                  <Phone className="mr-2 w-4 h-4" />
                  {SITE.phone}
                </a>
              </Button>
              <Button asChild className="bg-black/30 hover:bg-black text-white rounded-full px-6 py-6 font-medium border border-white/30">
                <a href="/#contact">
                  Formulaire de contact
                  <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
      <BackToTop />
    </div>
  );
}
